import { FC } from "react";
import Modal from "@Components/Modal";
import Portal from "@Components/Portal";
import Button from "@Components/Button";
import styles from "./RoomTypesModal.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import unitTypesDescription from "../../utils/unitTypesDescription";
import useTranslationHook from "hooks/useTranslationHook";
import useIsDesktop from "hooks/useIsDesktop";

interface RoomTypesModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableRoomTypes: string[];
}

const RoomTypesModal: FC<RoomTypesModalProps> = ({ isOpen, onClose, availableRoomTypes }) => {
  const { t } = useTranslationHook();
  const isDesktop = useIsDesktop();

  if (!isOpen) return null;

  const unitTypesDesc: any = unitTypesDescription(t);

  // Filter to only show available room types for this property
  const roomTypesToShow = availableRoomTypes
    .map((key: string) => ({
      key,
      icon: unitTypesDesc[key]?.icon,
      text: unitTypesDesc[key]?.text,
    }))
    .filter((item: any) => item.icon && item.text); // Only show items with icons and text

  // Format room type title with proper capitalization
  const formatRoomTypeTitle = (key: string) => {
    // For bedroom types (1b, 2b, 3b, etc.), keep the 'B' uppercase
    if (/^\d+b$/i.test(key)) {
      return key.toUpperCase();
    }
    // For other types, capitalize first letter of each word
    return key.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  return (
    <Portal>
      <Modal
        hideModal={onClose}
        title="Room Types"
        size="fullScreen"
        animation="bottomToTop"
        className={styles.modal}
        isHistoryPush={!isDesktop}
        type="RoomTypesModal"
        renderChildren={(handleHideModal: any) => (
          <div className={styles.modalContent}>
            <div className={styles.roomTypesList}>
              {roomTypesToShow.map((roomType, index) => (
                <div key={roomType.key}>
                  {index > 0 && <div className={styles.divider} />}
                  <div className={styles.roomTypeItem}>
                    <div className={styles.iconContainer}>{roomType.icon}</div>
                    <div className={styles.textContent}>
                      <div className={styles.title}>{formatRoomTypeTitle(roomType.key)}</div>
                      <div className={styles.description}>{roomType.text}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.footer}>
              <Button
                variant="primary"
                onClick={() => handleHideModal(true)}
                className={styles.okayButton}
                dataTestId="room-types-modal-okay-button"
              >
                Okay
              </Button>
            </div>
          </div>
        )}
      />
    </Portal>
  );
};

export default wrapperHOC(RoomTypesModal, {
  componentName: "RoomTypesModal-RoomTypeSection-PropertyPage",
  showForChina: true,
});
